/**
 * HomePage — Product landing + dashboard overview.
 *
 * Shows a hero section with the product value prop, live stats,
 * quick-action navigation tiles, and recent projects.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FolderOpen, ArrowRight, Sparkles, Download } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import apiClient from '../services/api'

interface Project {
  id: string
  name: string
  description: string | null
  status: string
  created_at: string
  updated_at: string | null
  asset_count: number
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function HomePage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [assembliesCount, setAssembliesCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const projRes = await apiClient.get('/projects/', { params: { limit: 200 } })
        if (cancelled) return

        const projs: Project[] = projRes.data.projects || []
        setProjects(projs)

        if (projs.length > 0) {
          try {
            const assemblyRes = await apiClient.get('/assemblies/', {
              params: { project_ids: projs.map(p => p.id).join(',') },
            })
            if (!cancelled) {
              setAssembliesCount(assemblyRes.data.total || assemblyRes.data.assemblies?.length || 0)
            }
          } catch {
            if (!cancelled) setAssembliesCount(0)
          }
        } else if (!cancelled) {
          setAssembliesCount(0)
        }
      } catch {
        // Silent — page still renders with zeros
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  const recentProjects = [...projects]
    .sort((a, b) => {
      const aTime = a.updated_at || a.created_at
      const bTime = b.updated_at || b.created_at
      return new Date(bTime).getTime() - new Date(aTime).getTime()
    })
    .slice(0, 5)

  const quickActions: Array<{ label: string; desc: string; path?: string; icon: LucideIcon; color: string; count?: number; onClick?: () => void }> = [
    { label: 'Assets', desc: 'View and organize source footage', path: '/projects', icon: FolderOpen, color: 'text-[var(--rq-blue)]' },
    { label: 'Create AI Draft', desc: 'Generate a new assembly from selected scope', path: '/create-ai-draft', icon: Sparkles, color: 'text-[var(--rq-amber)]' },
    { label: 'Assemblies', desc: 'Browse AI drafts, modify, and export', path: '/assemblies', icon: Download, color: 'text-emerald-400', count: assembliesCount },
  ]


  return (
    <div className="rq-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#161a22] via-[#1a1f2e] to-[#161a22] border border-white/5 px-8 py-12 mb-8">
        <div className="absolute inset-0 opacity-[0.07]" style={{ background: 'var(--rq-signal)' }} />
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-[var(--rq-blue)]/10 flex items-center justify-center mb-4">
            <FolderOpen className="w-8 h-8 text-[var(--rq-blue)]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[var(--rq-text)] mb-3 leading-tight">
            {projects.length > 0 ? 'Your workspaces' : 'No workspaces yet'}
          </h1>
          <p className="text-base text-[var(--rq-text-soft)] max-w-xl mb-6 leading-relaxed">
            {projects.length > 0
              ? `${projects.length} workspace${projects.length === 1 ? '' : 's'} — organize footage, generate AI drafts, and export to your NLE.`
              : 'Create your first workspace to start organizing footage.'}
          </p>
          <Link
            to="/projects"
            className="rq-btn-primary px-5 py-2.5 text-sm"
          >
            {projects.length > 0 ? 'View Workspaces' : 'Create Workspace'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--rq-text-soft)] mb-3">Quick Actions</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {quickActions.map(action => {
          const Icon = action.icon
          const content = (
            <>
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-white/10 transition-colors">
                <Icon className={`w-5 h-5 ${action.color}`} />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-semibold text-[var(--rq-text)]">{action.label}</p>
                  {action.count !== undefined && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-[var(--rq-text-soft)] tabular-nums">
                      {loading ? '—' : action.count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--rq-text-soft)] leading-relaxed">{action.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[var(--rq-text-soft)] opacity-0 group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0" />
            </>
          )

          if (action.onClick) {
            return (
              <button
                key={action.label}
                onClick={action.onClick}
                className="rq-card rq-card-hover px-5 py-5 flex items-start gap-4 group"
              >
                {content}
              </button>
            )
          }

          return (
            <Link
              key={action.path}
              to={action.path!}
              className="rq-card rq-card-hover px-5 py-5 flex items-start gap-4 group"
            >
              {content}
            </Link>
          )
        })}
      </div>

      {/* Recent Projects */}
      {recentProjects.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--rq-text-soft)]">Recent Workspaces</h2>
            <Link to="/projects" className="text-xs text-[var(--rq-blue)] hover:underline">
              View all
            </Link>
          </div>
          <div className="rq-card divide-y divide-white/5">
            {recentProjects.map(project => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.03] transition-colors first:rounded-t-xl last:rounded-b-xl"
              >
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="w-4 h-4 text-[var(--rq-blue)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--rq-text)] truncate">{project.name}</p>
                  {project.description && (
                    <p className="text-xs text-[var(--rq-text-soft)] truncate">{project.description}</p>
                  )}
                </div>
                <span className="text-xs text-[var(--rq-text-soft)] flex-shrink-0">
                  {project.asset_count} asset{project.asset_count !== 1 ? 's' : ''}
                </span>
                <span className="text-xs text-[var(--rq-text-soft)] flex-shrink-0 w-16 text-right">
                  {timeAgo(project.updated_at || project.created_at)}
                </span>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* Empty state for new users */}
      {!loading && projects.length === 0 && (
        <div className="rq-card px-8 py-12 text-center">
          <div className="flex items-center gap-3 justify-center mb-4">
            <Sparkles className="w-5 h-5 text-[var(--rq-amber)]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[var(--rq-amber)]">Structured Drafting for Editors</span>
          </div>
          <h3 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Assets In. Assembly Out.</h3>
          <p className="text-sm text-[var(--rq-text-soft)] mb-4">Repliqease proposes structure from selected footage so you can review, adjust, and export with confidence.</p>
          <Link to="/analysis" className="rq-btn-primary px-5 py-2.5 text-sm">
            Start analysing your footage
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  )
}
